import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Menu from '../../yoda-ui/components/Menu';
import Tooltip from '../../yoda-ui/components/Tooltip';
import CopyIcon from '../Icons/CopyIcon';
import DeleteTrashBinIcon from '../Icons/DeleteTrashBinIcon';
import EditPencilIcon from '../Icons/EditPencilIcon';
import MoveToIcon from '../Icons/MoveToIcon';
import MoreActionsMenuItem from '../MoreActionsMenu/MoreActionsMenuItem';
import { Channel, VeevaSyncStatus } from 'basics/enums/maya.enums';
import useTacticCard from 'components/TacticCard/TacticCard.hook';

import {
  CustomContentCardStyle,
  TacticCardFontStyle,
  TacticCardTitleStyle,
  clickableIdStyle,
  iconsStyle,
  tacticGroupIconStyle,
  navigationRowStyle,
  labelsWrapperStyle,
  clickableIdTitleStyle,
  clickableIdIconStyle,
  TacticCardInnerStyle,
  TacticCardSeparatorStyle,
  StepMoveToDropdownStyles,
  TacticDeleteButtonStyle,
} from 'components/TacticCard/TacticCard.styles';
import { TacticCardType } from 'components/TacticCard/TacticCard.types';
import Box from 'yoda-ui/components/Box';
import Card from 'yoda-ui/components/Card/Card';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Loader from 'yoda-ui/components/Loader';
import { YodaColors, YodaSpacing } from 'yoda-ui/yodaTheme';

const TacticCard: FC<TacticCardType> = ({
  tactic,
  allGroupedTactics,
  readOnly,
  isDeleteDisabled = false,
  isMoveDisabled = false,
  isTacticGroupingImprovementsEnabled = false,
  onMoveToStep,
  onCreateStepFromTacticAtStart,
  onCreateStepFromTactic,
  onTacticDeleted,
}) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const {
    deleteTacticCallback,
    cancelTacticCallback,
    editTacticCallback,
    getCategoryForChannel,
    getStyleFromCategory,
    getStyleFromVeevaSyncStatus,
    handleCopy,
    openModal,
    openModalCallback,
    removeTacticLoading,
    cancelTacticLoading,
    setOpenModal,
    extractNumberFromText,
    tacticGroup,
    getStylesFromNumberGroup,
    getCardTitle,
    menuItemConfigs,
    isDisabledMovingTactic,
    isOnlyTacticInOnlyStep,
    openMoveTacticMenu,
    setOpenMoveTacticMenu,
  } = useTacticCard({
    tactic,
    allGroupedTactics,
    isTacticGroupingImprovementsEnabled,
    onMoveToStep,
    onCreateStepFromTacticAtStart,
    onCreateStepFromTactic,
    onTacticDeleted,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const getAnchorPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const offset = 8;

      return {
        top: rect.bottom,
        left: rect.right - offset,
      };
    }
    return undefined;
  };

  const isLast = (index: number, arr: unknown[]) => index >= arr.length - 1;

  const menuItems = menuItemConfigs.map((step, index) => (
    <MoreActionsMenuItem
      key={ step.id }
      menuItemConfig={ step }
      isLast={ isLast(index, menuItemConfigs) }
      closeMenu={ () => setOpenMoveTacticMenu(false) }
    />
  ));

  const deleteDisabledTooltipTitle = String(t('maya_tactic_delete_disabled_tooltip'));
  const moveTacticDisabledTooltipTitle = String(t('maya_tactic_move_disabled_tooltip'));
  const moveOnlyTacticInOnlyStepTooltipTitle = String(t('maya_tactic_move_only_tactic_in_only_step_tooltip'));
  const isMoveTacticDisabled = isMoveDisabled || isDisabledMovingTactic;
  const moveTacticTooltipTitle = isOnlyTacticInOnlyStep
    ? moveOnlyTacticInOnlyStepTooltipTitle
    : moveTacticDisabledTooltipTitle;

  const deleteTacticButton = (
    <Box
      sx={ TacticDeleteButtonStyle(isDeleteDisabled) }
      onClick={
        () => {
          if (!isDeleteDisabled) {
            openModalCallback(tactic._id);
          }
        }
      }
    >
      <DeleteTrashBinIcon style={
        {
          color: isDeleteDisabled ? YodaColors.gray2 : YodaColors.blue7,
        }
      } />
    </Box>
  );

  const moveTacticButton = (
    <Box
      ref={ buttonRef }
      sx={
        {
          cursor: isMoveTacticDisabled
            ? 'not-allowed'
            : 'auto',
          pointerEvents: isMoveTacticDisabled
            ? 'none'
            : 'auto',
        }
      }
      onClick={ () => setOpenMoveTacticMenu(true) }
    >
      <MoveToIcon style={
        {
          color: isMoveTacticDisabled
            ? YodaColors.gray2
            : YodaColors.blue7,
        }
      }
      />
    </Box>
  );

  return (
    <>
      <Card
        key={ tactic._id }
        variant="outlined"
        sx={ CustomContentCardStyle }
      >
        <Box sx={ navigationRowStyle }>
          <Box sx={ labelsWrapperStyle }>
            <Box sx={ getStyleFromCategory(getCategoryForChannel(tactic.channel), tactic.channel) }>
              { getCategoryForChannel(tactic.channel) }
            </Box>
            {
              flags.mayaBdcfcsd1585EnableTacticGroup && tacticGroup
                ? <Box sx={ getStylesFromNumberGroup(extractNumberFromText(tacticGroup?.title)) }>
                  <LanOutlinedIcon sx={ tacticGroupIconStyle }></LanOutlinedIcon>
                  { extractNumberFromText(tacticGroup?.title) }
                </Box>
                : null
            }

            {
              readOnly ? null
                : <Box sx={ getStyleFromVeevaSyncStatus(tactic.veevaSyncStatus as VeevaSyncStatus) }>
                  { tactic.veevaSyncStatus }
                </Box>
            }
          </Box>

          {
            readOnly ? null
              : <Box sx={ iconsStyle } >
                {
                  isTacticGroupingImprovementsEnabled
                      && <>
                        {
                          isMoveTacticDisabled ? (
                            <Tooltip title={ moveTacticTooltipTitle }>
                              <div>
                                { moveTacticButton }
                              </div>
                            </Tooltip>
                          ) : moveTacticButton
                        }
                        <Menu
                          anchorPosition={ openMoveTacticMenu ? getAnchorPosition() : undefined }
                          anchorReference="anchorPosition"
                          anchorOrigin={
                            {
                              vertical: 'top',
                              horizontal: 'center',
                            }
                          }
                          transformOrigin={
                            {
                              vertical: 'top',
                              horizontal: 'center',
                            }
                          }
                          sx={ StepMoveToDropdownStyles }
                          open={ openMoveTacticMenu }
                          onClose={ () => setOpenMoveTacticMenu(false) }
                          anchorEl={ buttonRef.current }
                        >
                          { menuItems }
                        </Menu>
                      </>
                }
                <Box onClick={ () => editTacticCallback() }>
                  <EditPencilIcon style={ { color: YodaColors.blue7 } } />
                </Box>
                {
                  flags.mayaBiogenlinc3701EnableTacticDeleting
                    ? (
                      isDeleteDisabled ? (
                        <Tooltip title={ deleteDisabledTooltipTitle }>
                          <div>
                            { deleteTacticButton }
                          </div>
                        </Tooltip>
                      ) : deleteTacticButton
                    )
                    : null
                }
              </Box>
          }
        </Box>
        <Box marginTop={ YodaSpacing.small }>
          <Box sx={ TacticCardTitleStyle } >
            { `${getCardTitle(tactic)}` }
          </Box>
          <Box sx={ TacticCardInnerStyle }>
            <Box sx={ clickableIdStyle }>
              <Box sx={ clickableIdTitleStyle }>
                { `ID: ${tactic.formattedTacticId}` }
              </Box>
              {
                <Box sx={ clickableIdIconStyle } onClick={ () => handleCopy(tactic.formattedTacticId) }>
                  <CopyIcon style={ { color: YodaColors.blue7 } } />
                </Box>
              }
            </Box>
            <Box sx={ TacticCardSeparatorStyle } />
            <Box sx={ TacticCardFontStyle }>
              { `Channel: ${Channel[tactic.channel as unknown as keyof typeof Channel]}` }
            </Box>
            <Box sx={ TacticCardSeparatorStyle } />
            <Box sx={ TacticCardFontStyle } >
              {
                `Date: ${tactic.dateStart ? new Date(tactic.dateStart).toLocaleDateString('en-GB') : ''} 
                - ${tactic.dateEnd ? new Date(tactic.dateEnd).toLocaleDateString('en-GB') : ''}`
              }
            </Box>
          </Box>
        </Box>
      </Card>
      {
        openModal && (
          <ConfirmationDialog
            dialogTitle={
              flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
                ? t('maya_tactic_modal_cancel')
                : t('maya_tactic_modal_remove')
            }
            onConfirm={
              flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
                ? () => cancelTacticCallback(tactic._id)
                : () => deleteTacticCallback(tactic._id)
            }
            onConfirmLabel={
              removeTacticLoading || cancelTacticLoading
                ? <Loader center size={ 1 } color={ YodaColors.inherit } />
                : t('actions_confirm_label')
            }
            onDismiss={ () => setOpenModal(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
    </>
  );
};

export default TacticCard;
